import { LinearGradient } from 'expo-linear-gradient';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { RadioButton } from 'react-native-paper';

import { DarkModeIcon, SettingsIcon, SunnyIcon } from '@/assets';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { ListItem } from '@/features/list/components/ListItem';
import { ListSection } from '@/features/list/components/ListSection';
import { useAppSettingStore } from '@/features/profile-setting/store/useAppSettingStore';
import {
  Locale,
  LocalizationNameSpace
} from '@/localization/types/localizationTypes';
import { StateView } from '@/shared/components/state-view/StateView';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import { AppThemeMode } from '@/styles/type/appThemeMode';
import type { SVGComponent } from '@/types';

const APP_THEME_ICON: Record<AppThemeMode, SVGComponent> = {
  [AppThemeMode.DARK]: DarkModeIcon,
  [AppThemeMode.LIGHT]: SunnyIcon,
  [AppThemeMode.AUTO]: SettingsIcon
};

export const AppSettingScreen: FC = () => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.app-setting'
  });

  const { isFeatureEnabled } = useFeature();
  const isDarkModeEnabled = isFeatureEnabled(FeaturesEnum.DarkMode);

  const selectedTheme = useAppSettingStore((state) => state.selectedTheme);
  const updateSelectedTheme = useAppSettingStore(
    (state) => state.updateSelectedTheme
  );

  const selectedLocale = useAppSettingStore((state) => state.selectedLocale);
  const updateSelectedLocale = useAppSettingStore(
    (state) => state.updateSelectedLocale
  );

  return (
    <LinearGradient
      style={{ flex: 1 }}
      colors={[
        theme.colors.surfaceContainer,
        theme.colors.secondaryContainer,
        theme.colors.secondaryContainer
      ]}
    >
      <StateView
        backgroundColor="transparent"
        contentContainerStyle={{ paddingTop: 8, paddingBottom: 32 }}
      >
        {isDarkModeEnabled && (
          <ListSection
            title={t('appearance')}
            titleVariant="titleLarge"
            style={{ backgroundColor: 'transparent' }}
            headerBackgroundColor="transparent"
          >
            {Object.values(AppThemeMode).map((appTheme) => (
              <ListItem
                key={appTheme}
                title={t(appTheme)}
                onPress={() => updateSelectedTheme(appTheme)}
                icon={APP_THEME_ICON[appTheme]}
                arrowIcon={() => (
                  <View style={{ margin: -8 }}>
                    <RadioButton
                      onPress={() => updateSelectedTheme(appTheme)}
                      value={appTheme}
                      status={
                        selectedTheme === appTheme ? 'checked' : 'unchecked'
                      }
                    />
                  </View>
                )}
                backgroundColor={theme.colors.surfaceContainerLowest}
              />
            ))}
          </ListSection>
        )}
        <ListSection
          title={t('language')}
          titleVariant="titleLarge"
          style={{ backgroundColor: 'transparent' }}
          headerBackgroundColor="transparent"
        >
          {Object.values(Locale).map((locale) => (
            <ListItem
              key={locale}
              title={t(locale)}
              description={t(`translated.${locale}`)}
              textOrder="column-reverse"
              onPress={() => updateSelectedLocale(locale)}
              arrowIcon={() => (
                <View style={{ margin: -8 }}>
                  <RadioButton
                    onPress={() => updateSelectedLocale(locale)}
                    value={locale}
                    status={selectedLocale === locale ? 'checked' : 'unchecked'}
                  />
                </View>
              )}
              backgroundColor={theme.colors.surfaceContainerLowest}
            />
          ))}
        </ListSection>
      </StateView>
    </LinearGradient>
  );
};
